import { IssueTypeT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import showErrorToast from '@/lib/helpers/showToastError.tsx'
import { CurrStageData } from '@/lib/actions/getCurrStage.ts'
import { useLocallyStoredFilters } from '@/routes/projectStages/issues/lib/stores/useLocallyStoredFilters.ts'
import { useUserStore } from '@/store/user.ts'
import createIri from '@/lib/helpers/createIri.ts'

export default function createIssueTypesLinks(
  currProjectStage: CurrStageData | null | undefined,
  issueTypes: IssueTypeT[] | null | undefined,
) {
  const stageId = currProjectStage?.id
  const stageIri = createIri('project_stages', stageId)

  let unreadNotificationsCount = 0
  const defaultLinks = issueTypes?.map(issueType => {
    const notificationsCount =
      issueType.unreadNotificationsCount && issueType.unreadNotificationsCount > 0
        ? issueType.unreadNotificationsCount
        : null
    unreadNotificationsCount += notificationsCount ?? 0

    if (!stageId) showErrorToast(`link creation failed - stageId not found`)

    const defView = issueType?.defaultListView || 'COLUMNS'
    const issueTypeIri = `/issue_types/${issueType?.id}`
    const defaultLink = `${stageIri}${issueTypeIri}/issues?view=${defView}`

    return {
      issueTypeIri: issueTypeIri as string,
      name: issueType.name,
      color: issueType.color,
      link: defaultLink,
      notifications: notificationsCount,
    }
  })

  // Try to pull any per-link URLs previously saved for this stage (these
  // carry filter/sort state the user has applied and want preserved on
  // navigation).
  // Merge any per-issue-type filter URLs the user has saved locally on top of
  // the fresh API set. We enumerate from `defaultLinks` (so newly added types
  // appear and removed ones disappear) and only override `link` from storage
  // — name/color/notifications always come from the server.
  const allStoredTabs = useLocallyStoredFilters.getState().tabs
  const userId = useUserStore.getState().user?.id
  const curTab = allStoredTabs.find(
    tab => String(tab.projectStageId) === String(stageId) && tab.userId === userId,
  )
  const storedLinks = curTab?.projectStageIssuesLinks

  // Merge strategy:
  //
  // Source of truth = `defaultLinks` (freshly computed from the current
  // issueTypes payload — correct names, colors, notification counts, and
  // the full set of issue types the stage currently has).
  //
  // From `storedLinks` we only carry the `link` URL over when the
  // issueTypeIri matches, so a user's filtered/sorted list URL isn't lost
  // on refresh.
  //
  // Why not the previous `storedLinks || defaultLinks` fallback: an empty
  // array is truthy in JS, so if storedLinks was ever persisted as `[]`
  // (happens when useNavStagesData.onSelect runs before the issue-types
  // cache is ready and calls updateActiveTab with an empty links array,
  // which can be triggered by adding a new stage in project settings) the
  // `||` short-circuited to `[]` and wiped the menu's issue-type rows for
  // the whole stage, even though fresh defaults existed. Merging avoids
  // that class of bug entirely and also drops stale entries for deleted
  // issue types.
  const storedByIri = new Map(
    (storedLinks ?? []).map(stored => [stored.issueTypeIri, stored]),
  )
  const mergedLinks = defaultLinks?.map(fresh => {
    const stored = storedByIri.get(fresh.issueTypeIri)
    return stored?.link ? { ...fresh, link: stored.link } : fresh
  })

  return {
    projectStageIssuesLinks: mergedLinks,
    unreadNotificationsCount: unreadNotificationsCount,
  }
}
